import type {
  FreeFormObject,
  ModuleSnapshot,
  ReagentDef,
  ReagentLot,
  RecipeLabwareDef,
} from '@ctcells/contract'

import type {
  DeckRackKind,
  RackOccupancy,
  RackReagents,
  WellFill,
} from '../../figures/DeckRackFigure.tsx'
import { bundledSlotOrder, rackCapacity } from '../../figures/deckRackDefinitions.ts'

import {
  deckOccupancy,
  tcRemaining,
  tcUsedCells,
  tipRackUsed,
  tipRackUsedSlots,
  tipsRemaining,
  wellVolume,
} from './deckOccupancy.ts'
import { deckRacksOf, definitionAt, sameLabware, type DeckRack } from './deckRacks.ts'
import { findModule } from './monitoringModel.ts'

/**
 * **소모품 요약 — 모니터링 시료 탭의 요약 줄과 오른쪽 서랍이 같이 읽는 한 벌**(eric 2026-09-27 원문:
 * 「모니터링 화면에 현재 시약 분비(분주) 상태, 그리고 팁랙의 현재 상황, 전체 개수 중에 몇 개 차 있나 확인하는
 * 대시보드 만들고, 클릭하면 해당 상세 내용이 나오도록.」).
 *
 * ## 🔴 모르면 «가득»이다 — 그러나 «가득이라고 본 값»이라고 적는다
 *
 * 앞 지시(「상황을 모르면 일단 다 채워져 있는 것으로」)를 그대로 따른다. 다만 기본값을 사실처럼 보이지 않게
 * 항목마다 **출처(`origin`)** 를 싣는다:
 *
 * | `origin`  | 뜻 |
 * | --------- | --- |
 * | `live`    | 장비가 보고한 값(스냅샷 `consumables`) |
 * | `default` | 장비 값이 없어 **가득으로 본 값** — 화면이 「장비 값 미수신」 표지를 단다 |
 * | `partial` | 시약 중 일부 웰만 보고됐다 |
 *
 * ## 출처 (2026-09-27 실측 · `GET /modules`)
 *
 * - 팁 — 🔴 합계(`tips_remaining`)가 0 이거나 칸 전부면 **합계가 칸별 기록을 이긴다**(아래 `totalDecides`).
 *   그 밖에는 염색기 `tips_by_rack[]`(랙별 쓴 칸) 먼저, 둘이 어긋나면 `mismatch`. 합계만 오면 **랙별로 나누지
 *   않는다** — 합계가 칸 전부(= 다 가득)이거나 0 이거나, 모르는 랙이 하나뿐일 때만 그 랙의 수가 정해진다.
 *   그 밖에는 `remaining: null`(랙별 미수신)이고 합계는 `tipsReported` 가 나른다.
 * - TC 적재소 — 어셈블러 `tc_remaining`
 * - 시약 — 레시피 `payload.reagents[].volumeByWell` 이 «시약을 둔 웰»(계획), 염색기 `reagents_by_well[]` 의
 *   잔량이 «채움 여부»의 출처. 레시피가 없으면(도는 작업 없음) 시약을 모른다 — 지어내지 않는다
 *
 * ## 🔴 시약은 «채움 여부»만 — 부피를 적지 않는다 (eric 확정 2026-09-28)
 *
 * eric 원문: 「각각의 랙의 해당 웰의 용액 값은 없애줘.. 그냥 저렇게까지는 필요없고. 채웠냐. 안채웠냐만 알면 돼..」
 * ⇒ 웰마다 `filled`(잔량 > 0) 하나다. µL · mL 합계는 걷었다 — 되살리지 마라. 팁 · TC 의 칸 수(96/96 · 9/9)는 그대로다
 *   (eric 이 뺀 것은 용액 값이다).
 *
 * ## 🔴 시약은 «시약 하나당 있다/없다» — 웰 번호가 아니다 (eric 2026-10-02)
 *
 * eric 원문: 「저렇게 숫자 말고... 실제 레시피에서 사용되는 시약으로 … 한 줄에 하나씩.. 시약은 있냐 없냐로만 판단하면 됨..」
 * ⇒ 시약마다 `presence` 하나(`reagentPresence()` 한 곳)이고, 항목 값은 **시약 종류 수**로 센다(`unit: 'reagents'`):
 *   - `present` 있음 — 장비가 보고한 웰 중 하나라도 채워져 있다
 *   - `absent` 없음 — 장비가 보고한 웰이 전부 비었다
 *   - `unreceived` 미수신 — 그 시약의 웰을 장비가 하나도 보고하지 않았다. 🔴 「못 봤다」를 있음 · 없음으로 접지 않는다
 *   ⚠ 일부 웰만 보고되면 **보고된 웰만으로** 판정한다(보고 안 된 웰을 «가득»으로 세어 「없음」을 덮지 않는다).
 *   항목 값 `remaining` = **있음인 시약 수만**(eric 2026-10-02 「1/8개 · 미수신 7 … 이게 맞아 보이는데?」) — 미수신을 «있는 것»으로
 *   세지 않는다(종전 「없음이 아닌 시약 수」는 「8/8개 · 미수신 7」로 떠서 못 본 시약이 있는 것처럼 읽혔다). 미수신은 머리의
 *   「미수신 N」이 따로 말한다 ⇒ 있음 + 없음 + 미수신 = `total`. 전부 미수신이면 「0/8개 · 미수신 8」.
 *   ⚠ 시약 항목의 `origin`(「모르면 가득」의 출처 표지)은 그대로 싣는다 — 툴팁 · 보조기술 글자가 쓴다.
 *   종전 웰 단위 값(「시약 14/14」 · 웰 7열 번호 격자)은 걷었다 — 되살리지 마라.
 *   ⚠ 최근 분주 기록의 분주량은 «기록 값»이라 그대로 적는다(서랍 `DispenseTable`).
 * - 칸 수 — 랩웨어 정의(`rackCapacity`). 96 · 9 를 여기 박지 않는다
 *
 * ⚠ **폐기통은 없다.** `GET /waste-containers` 의 `WasteContainer` 에 **채움 칸이 없다**(`capacity` ·
 *   `alarm_threshold_pct` 뿐 · 실서버 `openapi.json` 2026-09-27) — 「몇 % 찼나」를 말할 값이 계약에 없다.
 *   목록도 0건이다(BR-33). 채움 값이 계약에 들어오면 여기 항목으로 더한다.
 * 🔴 **계산은 여기서만 한다.** 화면 · 서랍은 받은 값을 그린다. 칸 하나하나의 상태(`full` · `used`)는
 *    `deckOccupancy()` 가, `consumables` 의 모양 풀이는 같은 파일의 읽개들이 정한다.
 */

/**
 * **소모품 차례 — 한 곳이다**(eric 2026-09-28 「어셈블러 TC 적재소, 염색기 팁 랙 1, 팁 랙 2, 시약 — 이렇게
 * 표현해줘」). 공정 차례(어셈블러 → 염색기)다. 요약 줄 · 서랍 머리의 항목 줄 · `items` 가 전부 이 차례를 읽는다.
 * 🔴 모듈 묶음(`module`)은 이 차례에서 이웃한 항목끼리 갈린다 — 같은 모듈의 키를 떨어뜨려 놓지 마라.
 */
export const SUPPLY_KEYS = ['tcstock', 'tips-1', 'tips-2', 'reagents'] as const
export type SupplyKey = (typeof SUPPLY_KEYS)[number]

export function isSupplyKey(value: string | undefined): value is SupplyKey {
  return value !== undefined && (SUPPLY_KEYS as readonly string[]).includes(value)
}

export type SupplyOrigin = 'live' | 'default' | 'partial'

export interface SupplyItem {
  key: SupplyKey
  module: 'asm' | 'stain'
  /** 서랍이 그릴 랙 — 데크 표(`deckRacks.ts`)의 그 줄 그대로 */
  racks: readonly DeckRack[]
  /**
   * 남은 수 — 팁 · TC 는 칸, 시약은 **있음인 시약 수**(웰 수 · 부피가 아니다 · 미수신은 안 센다 · 위 「시약 하나당 있다/없다」).
   * 🔴 `null` = 모른다(팁 합계만 와서 랙별로 못 나눈다 · 시약은 도는 작업 없음). 가득으로 접지 않는다
   */
  remaining: number | null
  /** 전체 — 팁 · TC 는 칸 수, 시약은 레시피의 시약 종류 수(`payload.reagents` · 레시피가 없으면 0) */
  total: number
  /** `count` = 칸 수(팁 · TC) · `reagents` = 시약 종류 수(시약) */
  unit: 'count' | 'reagents'
  origin: SupplyOrigin
  /** 그 값을 준 모듈 스냅샷이 낡았다(`ModuleSnapshot.stale`) */
  stale: boolean
  /**
   * 🔴 팁 — 칸별 기록(`tips_by_rack`)과 합계(`tips_remaining`)가 맞지 않는다. 이때 `remaining` 은 `null` 이고
   * (어느 쪽도 지어내지 않는다) 합계는 `SupplySummary.tipsReported` 가 나른다
   */
  mismatch?: boolean | undefined
  /**
   * 🔴 팁 랙 — **장비가 칸으로 보고한 쓴 칸의 웰 이름**(`tips_by_rack[].used_slots` → 레시피 랩웨어 `well_names[N-1]` ·
   * 레시피가 없으면 랙 정의의 기본 차례 `bundledSlotOrder()[N-1]` · `deckOccupancy.ts` 머리말 1번 · 2026-10-01 · 2026-10-26).
   * 그 랙의 남은 수가 칸 기록에서 나왔을 때(`exact`)만 선다 — 합계가 이긴 경우 · 불일치 · 레시피 정의는 있는데 `well_names` 가
   * 없을 때 · 범위 밖 색인이 섞였을 때는 없다(칸을 지어내지 않는다).
   * 🔵 TC 적재소 — 장비가 칸을 보고하지 않아 남은 수로 정해진 차례(A1 → C3) 앞에서부터 쓴 칸의 그림 열쇠('1'…'9')다
   * (`tcUsedCells` · eric 2026-10-29 · 백엔드 요청 BR 대기).
   * 칸 맵(`rackOccupancy`)이 이것으로 쓴 칸만 비운다.
   */
  usedWells?: readonly string[] | undefined
  /**
   * 그 랙의 **도는 작업 레시피 랩웨어 정의**(`payload.labware[]` · `definitionAt`) — 판의 랙 그림(`DeckRackFigure`)이 칸 수 · 자리를
   * 이 정의로 그린다(레시피가 먼저 · 없으면 그림이 번들 사본). 칸 수(`total`)와 같은 출처라 그림과 수치가 어긋나지 않는다.
   * 시약 항목에는 없다(줄 목록이다).
   */
  definition?: FreeFormObject | undefined
}

export interface ReagentWellSupply {
  labwareId: string
  well: string
  /**
   * 채워져 있나 — 장비가 보고한 잔량이 0 보다 크면 채움, 0 이면 비어 있음. 보고가 없으면(`default`)
   * 레시피가 그 웰에 담을 부피가 있으면 채움으로 본다(「모르면 가득」 · 화면이 「장비 값 미수신」 표지를 단다)
   */
  filled: boolean
  origin: 'live' | 'default'
}

/** 시약 하나의 있다/없다 — `reagentPresence()` 가 정한다(위 머리말) */
export type ReagentPresence = 'present' | 'absent' | 'unreceived'

export interface ReagentSupply {
  /** `payload.reagents` 의 키(채널 번호 문자열) */
  key: string
  channel: number
  /** 화면 이름 — `display_name` 먼저 */
  name: string
  /** 성분명(`name`) — 화면 이름과 다를 때만 덧붙인다 */
  substance: string
  labwareId: string | undefined
  wells: readonly ReagentWellSupply[]
  /** 있다 · 없다 · 미수신 — 화면은 이 값을 그린다(셈은 `reagentPresence()` 한 곳) */
  presence: ReagentPresence
  /** 레시피의 보관 온도(`storage_celsius` · ℃). 없으면 `undefined` */
  storageCelsius: number | undefined
  /**
   * 레시피의 시약 표시색(`display_color`) — 랙 그림의 뚜껑 테에 얹는다(`DeckRackFigure` `reagents`). 없으면 키가 없다(무채색 테).
   * ⚠ 실물 레시피에는 아직 0건이다(`deckReagentsAt` 머리말)
   */
  color?: string | undefined
  origin: SupplyOrigin
  /** 레시피가 적은 lot(빈 문자열이면 `undefined`) */
  lot: string | undefined
  /** `GET /reagents/lots` 에서 그 lot 을 찾았으면 */
  lotRecord: ReagentLot | undefined
}

/**
 * **시약대 한 칸 — 그 시약대에 놓인 시약들**(eric 2026-10-02 「시약대 별로... 리스트를 적어줘」).
 * 시약 하나는 한 시약대에만 있다(레시피 `reagents[].labware_id` 하나 · 실물 확인). 이름은 화면이 짓는다(`reagentBayName()` · `supplyText.ts`).
 */
export interface ReagentBay {
  /** 레시피 `labware_id` — 시약이 그 칸을 안 적었으면 `undefined`(시약대 미지정 한 묶음) */
  labwareId: string | undefined
  /** 레시피 `labware[].display_name` — 없으면 `undefined`(표준 레시피의 시약대 셋은 없다 · 화면이 `labware_id` 로 짓는다) */
  displayName: string | undefined
  /** 그 시약대의 시약이 «전부» 같은 보관 온도면 그 값 — 소제목이 한 번 말하고 줄마다 적지 않는다. 섞이거나 없으면 `undefined` */
  storageCelsius: number | undefined
  /** 채널 차례 */
  reagents: readonly ReagentSupply[]
  /**
   * 그 시약대의 **도는 작업 레시피 랩웨어 정의**(`payload.labware[]` · `definitionAt`) — 시약 카드의 랙 그림(`DeckRackFigure`)이
   * 칸 자리를 이 정의로 그린다(팁 · TC 의 `SupplyItem.definition` 과 같은 출처). 없으면 그림이 번들 사본으로 선다.
   */
  definition?: FreeFormObject | undefined
}

export interface SupplySummary {
  items: readonly SupplyItem[]
  reagents: readonly ReagentSupply[]
  /**
   * 시약대별 묶음 — 차례는 레시피 `labware` 차례(표준 레시피: 상온 1 → 상온 2 → 냉장). 레시피 `labware` 에 없는 시약대는
   * 그 뒤에 첫 시약의 채널 차례로. 🔴 같은 `reagents` 를 다시 묶은 것뿐이다 — 판정 · 수는 `reagents` 와 같다
   */
  reagentBays: readonly ReagentBay[]
  /** 팁 합계(`tips_remaining`)가 왔으면 그 값과 칸 수 합 — 랙별로 못 나눴을 때 서랍 · 요약이 적는다 */
  tipsReported: { remaining: number; total: number } | undefined
  /**
   * 염색기 시약 보관 설정 온도(`ModuleSnapshot.reagent_storage.setpoint_celsius` · ℃) — 냉장 시약대(cold rack)의 «장비 설정값».
   * 숫자가 아니면(`null` · 키 없음 · 능력 없는 모듈) `undefined`. 랙 선택지의 「4℃」가 레시피 값이 없을 때 이 값을 쓴다(`reagentRackChoices`).
   * ⚠ 실측값(`actual_celsius`)이 아니다 — 측정 온도는 장비 탭 소관이다
   */
  storageSetpointCelsius: number | undefined
}

export interface SupplySummaryInput {
  modules: readonly ModuleSnapshot[]
  /** 도는 작업 레시피 `payload.reagents` */
  reagents?: Readonly<Record<string, ReagentDef>> | undefined
  /** 같은 레시피 `payload.labware` — 칸 수의 출처 */
  labware?: readonly RecipeLabwareDef[] | undefined
  /** `GET /reagents/lots` — 없으면(못 봤으면) lot 풀이를 건너뛴다 */
  lots?: readonly ReagentLot[] | undefined
}

export function supplySummary({
  modules,
  reagents,
  labware,
  lots,
}: SupplySummaryInput): SupplySummary {
  const stain = findModule(modules, 'stain')
  const asm = findModule(modules, 'asm')
  const stainBag = stain?.consumables
  const stainRacks = deckRacksOf('stain')

  /* ── 팁 랙 ── */
  const tipRacks = stainRacks.filter((rack) => rack.kind === 'tips')
  const caps = tipRacks.map((rack) => rackCapacity('tips', definitionAt(labware, rack.labwareId)))
  const capSum = caps.reduce((a, b) => a + b, 0)
  const reported = tipsRemaining(stainBag)
  const known = tipRacks.map((rack, i) => {
    const cap = caps[i] ?? 0
    const used = tipRackUsed(stainBag, rack.labwareId, cap)
    return used === undefined ? undefined : cap - used
  })
  /* 칸별 보고가 없는 랙들 — 합계에서 아는 랙을 뺀 나머지가 그 랙들의 몫이다 */
  const unknownIdx = known.flatMap((value, i) => (value === undefined ? [i] : []))
  const unknownCap = unknownIdx.reduce((a, i) => a + (caps[i] ?? 0), 0)
  const residual =
    reported === undefined ? undefined : reported - known.reduce<number>((a, v) => a + (v ?? 0), 0)
  /*
   * 🔴 **합계가 0 이거나 칸 전부면 합계가 이긴다**(2026-09-28 실측: API 로 192 보충 뒤 `tips_remaining: 192` 인데
   *    `tips_by_rack` 은 rack a 1~96 · rack b 1~21 을 여전히 used 로 들고 있었다 — 시뮬레이터가 보충 때 칸 기록을
   *    안 지운다). 합계가 칸 전부면 어느 칸이 비었는지 고를 필요가 없다 — 두 랙 다 가득이다. 0 도 같다.
   * 🔴 **그 사이 값에서 둘이 어긋나면 어느 쪽도 지어내지 않는다** — 랙별 수는 `null`, 합계는 `tipsReported`,
   *    항목에 `mismatch` 표지(「칸별 기록과 합계 불일치」).
   */
  const totalDecides =
    reported !== undefined && capSum > 0 && (reported === capSum || reported === 0)
  const mismatch =
    !totalDecides &&
    residual !== undefined &&
    known.some((value) => value !== undefined) &&
    (unknownIdx.length === 0 ? residual !== 0 : residual < 0 || residual > unknownCap)
  const tipItems = tipRacks.map((rack, i): SupplyItem => {
    const cap = caps[i] ?? 0
    const definition = definitionAt(labware, rack.labwareId)
    const exact = known[i]
    let remaining: number | null
    let origin: SupplyOrigin
    if (totalDecides) {
      remaining = reported === 0 ? 0 : cap
      origin = 'live'
    } else if (mismatch) {
      remaining = null
      origin = 'live'
    } else if (exact !== undefined) {
      remaining = exact
      origin = 'live'
    } else if (residual === undefined) {
      remaining = cap
      origin = 'default'
    } else {
      origin = 'live'
      remaining =
        residual === unknownCap
          ? cap
          : residual === 0
            ? 0
            : unknownIdx.length === 1 && residual > 0 && residual <= cap
              ? residual
              : null
    }
    /*
     * 🔴 쓴 칸의 «자리» — 남은 수가 이 랙의 칸 기록에서 나왔을 때만(`exact` 갈래). 색인을 웰 이름으로 푸는 표는
     *    ① 도는 작업 레시피 랩웨어의 `well_names`(L2 `TipPlan` 이 그 차례로 칸을 고른다 · `pick_order` 반영) ② 레시피가 없으면
     *    **랙 정의(번들 사본)의 기본 차례**(`bundledSlotOrder` — 서버 `well_order()` 기본값과 같은 열 차례 · 2026-10-26 팀장 결정).
     *    레시피 정의가 «있는데» 표가 없으면 ②로 내려가지 않는다(그 레시피의 차례를 모른다). 하나라도 못 풀면 통째로 안 싣는다 —
     *    그때는 종전 규칙(가득 + 「칸별 사용 기록 미수신」 · eric 2026-09-28)이다.
     */
    const usedWells =
      !totalDecides && !mismatch && exact !== undefined
        ? slotWells(
            tipRackUsedSlots(stainBag, rack.labwareId, cap),
            definition === undefined ? bundledSlotOrder(rack.kind) : definition['well_names'],
          )
        : undefined
    return {
      key: i === 0 ? 'tips-1' : 'tips-2',
      module: 'stain',
      racks: [rack],
      remaining,
      total: cap,
      unit: 'count',
      origin,
      stale: stain?.stale === true,
      ...(mismatch ? { mismatch: true } : {}),
      ...(usedWells === undefined ? {} : { usedWells }),
      ...(definition === undefined ? {} : { definition }),
    }
  })

  /* ── 시약 ── */
  const reagentRows = Object.entries(reagents ?? {})
    .map(([key, def]) => reagentSupply(key, def, stainBag, lots))
    .sort((a, b) => a.channel - b.channel)
  /* 🔴 시약 수로 센다 — 있음인 시약만(미수신 · 없음은 안 센다 · 위 머리말) / 시약 수 */
  const reagentItem: SupplyItem = {
    key: 'reagents',
    module: 'stain',
    racks: stainRacks.filter((rack) => rack.kind !== 'tips'),
    remaining:
      reagentRows.length === 0 ? null : reagentRows.filter((r) => r.presence === 'present').length,
    total: reagentRows.length,
    unit: 'reagents',
    origin: mergeOrigin(reagentRows.map((r) => r.origin)),
    stale: stain?.stale === true,
  }

  /* ── TC 적재소 ── */
  const tcRack = deckRacksOf('asm').find((rack) => rack.kind === 'tcstock')
  const tcItems: SupplyItem[] =
    tcRack === undefined
      ? []
      : [
          (() => {
            const definition = definitionAt(labware, tcRack.labwareId)
            const cap = rackCapacity('tcstock', definition)
            const value = tcRemaining(asm?.consumables)
            /* 사이 값은 정해진 차례로 앞에서부터 비운다(`tcUsedCells` · eric 2026-10-29) — 장비 값이 왔을 때만 */
            const usedWells = value === undefined ? undefined : tcUsedCells(value, cap, definition)
            return {
              ...(definition === undefined ? {} : { definition }),
              key: 'tcstock',
              module: 'asm',
              racks: [tcRack],
              remaining: value ?? cap,
              total: cap,
              unit: 'count',
              origin: value === undefined ? 'default' : 'live',
              stale: asm?.stale === true,
              ...(usedWells === undefined ? {} : { usedWells }),
            } satisfies SupplyItem
          })(),
        ]

  return {
    items: [...tipItems, reagentItem, ...tcItems].sort(
      (a, b) => SUPPLY_KEYS.indexOf(a.key) - SUPPLY_KEYS.indexOf(b.key),
    ),
    reagents: reagentRows,
    reagentBays: reagentBays(reagentRows, labware),
    tipsReported: reported === undefined ? undefined : { remaining: reported, total: capSum },
    storageSetpointCelsius:
      typeof stain?.reagent_storage?.setpoint_celsius === 'number'
        ? stain.reagent_storage.setpoint_celsius
        : undefined,
  }
}

/** 시약 줄(채널 차례)을 시약대별로 묶는다 — 차례는 레시피 `labware` 차례, 거기 없는 시약대는 뒤에 첫 채널 차례로 */
function reagentBays(
  rows: readonly ReagentSupply[],
  labware: readonly RecipeLabwareDef[] | undefined,
): ReagentBay[] {
  const groups = new Map<string | undefined, ReagentSupply[]>()
  for (const row of rows) {
    const group = groups.get(row.labwareId)
    if (group === undefined) groups.set(row.labwareId, [row])
    else group.push(row)
  }
  const rank = (id: string | undefined) => {
    const i = id === undefined ? -1 : (labware ?? []).findIndex((def) => def.id === id)
    return i < 0 ? Number.MAX_SAFE_INTEGER : i
  }
  return [...groups.entries()]
    .map(([labwareId, reagents]): ReagentBay => {
      const temps = new Set(reagents.map((r) => r.storageCelsius))
      const [only] = [...temps]
      const name = labware?.find((def) => def.id === labwareId)?.display_name?.trim()
      const definition = labwareId === undefined ? undefined : definitionAt(labware, labwareId)
      return {
        labwareId,
        displayName: name === undefined || name === '' ? undefined : name,
        storageCelsius: temps.size === 1 ? only : undefined,
        reagents,
        ...(definition === undefined ? {} : { definition }),
      }
    })
    .sort(
      (a, b) =>
        rank(a.labwareId) - rank(b.labwareId) ||
        (a.reagents[0]?.channel ?? 0) - (b.reagents[0]?.channel ?? 0),
    )
}

/**
 * 쓴 칸 색인(1-기반) → 웰 이름(`well_names[N-1]`). 색인이 없거나(`undefined`) 표가 배열이 아니거나 하나라도 못 풀면
 * `undefined` — 반만 풀어 칸 맵을 세우면 못 푼 칸이 «가득»으로 남아 거짓이 된다.
 */
function slotWells(slots: readonly number[] | undefined, names: unknown): string[] | undefined {
  if (slots === undefined || !Array.isArray(names)) return undefined
  const wells: string[] = []
  for (const slot of slots) {
    const name: unknown = names[slot - 1]
    if (typeof name !== 'string') return undefined
    wells.push(name)
  }
  return wells
}

function reagentSupply(
  key: string,
  def: ReagentDef,
  bag: ModuleSnapshot['consumables'],
  lots: readonly ReagentLot[] | undefined,
): ReagentSupply {
  const labwareId = def.labware_id
  const wells = Object.entries(def.volumeByWell ?? {}).map(
    ([well, filledUl]): ReagentWellSupply => {
      const live = labwareId === undefined ? undefined : wellVolume(bag, labwareId, well)
      return {
        labwareId: labwareId ?? '',
        well,
        filled: (live ?? filledUl) > 0,
        origin: live === undefined ? 'default' : 'live',
      }
    },
  )
  const lot = def.lot === undefined || def.lot.trim() === '' ? undefined : def.lot
  return {
    key,
    channel: def.channel,
    name: def.display_name ?? def.name,
    substance: def.name,
    labwareId,
    wells,
    presence: reagentPresence(wells),
    storageCelsius: typeof def.storage_celsius === 'number' ? def.storage_celsius : undefined,
    ...(def.display_color === undefined ? {} : { color: def.display_color }),
    origin: mergeOrigin(wells.map((w) => w.origin)),
    lot,
    lotRecord: lot === undefined ? undefined : lots?.find((row) => row.lot === lot),
  }
}

/**
 * **시약 하나의 있다/없다**(eric 2026-10-02 「시약은 있냐 없냐로만 판단하면 됨」 · 위 머리말).
 * 장비가 보고한 웰(`origin: 'live'`)만 본다 — 하나라도 채움이면 있음, 전부 비면 없음, 보고된 웰이 없으면 미수신.
 * 🔴 레시피 부피로 «가득으로 본» 웰(`default`)은 판정에 넣지 않는다 — 넣으면 장비가 비었다고 보고한 시약이 「있음」으로 덮인다.
 */
export function reagentPresence(wells: readonly ReagentWellSupply[]): ReagentPresence {
  const reported = wells.filter((well) => well.origin === 'live')
  if (reported.length === 0) return 'unreceived'
  return reported.some((well) => well.filled) ? 'present' : 'absent'
}

/**
 * **칸 하나의 있다/없다**(eric 2026-10-02 「A1, A2, A3 이거.. 각각 있음 없음 나오게 표현해줘」) — 장비가 그 칸을 보고했으면
 * (`origin: 'live'`) 잔량 > 0 → 있음 · 0 → 없음, 보고하지 않았으면 미수신. 🔴 레시피 부피로 «가득»을 지어내지 않는다
 * (`filled` 의 기본값은 판정에 안 쓴다). 시약 하나의 판정(`reagentPresence`)과 같은 규칙을 칸 하나에 건 것이다.
 */
export function wellPresence(well: Pick<ReagentWellSupply, 'filled' | 'origin'>): ReagentPresence {
  if (well.origin !== 'live') return 'unreceived'
  return well.filled ? 'present' : 'absent'
}

/** 전부 live → live · 하나도 없으면 default · 섞이면 partial. 빈 목록은 default(아무것도 보고받지 않았다) */
function mergeOrigin(origins: readonly SupplyOrigin[]): SupplyOrigin {
  if (origins.length === 0) return 'default'
  if (origins.every((o) => o === 'live')) return 'live'
  if (origins.every((o) => o === 'default')) return 'default'
  return 'partial'
}

/** 남은 비율(0~100). 모르거나 전체가 0 이면 `undefined` — 막대를 안 그린다 */
/**
 * 그 항목의 «장비가 보고한 칸 수» — 칸 맵 예외(0 이면 전부 비움 · 가득이면 전부 가득)의 입력.
 * 칸 수(팁 · TC)이고 장비 값(`live`)이고 수를 알 때만 준다. 그 밖(미수신 · 랙별 미수신 · 시약)은 `undefined`.
 */
export function reportedCount(
  item: SupplyItem | undefined,
): { remaining: number; total: number } | undefined {
  if (item === undefined) return undefined
  if (item.unit !== 'count' || item.origin !== 'live' || item.remaining === null) return undefined
  return { remaining: item.remaining, total: item.total }
}

/**
 * **그 랙의 칸 상태 — 소모품 서랍과 콘솔 장비 탭(`FocusFigure` 의 `realFigures`)이 같이 부른다.**
 *
 * 🔴 두 곳이 다른 말을 하지 않게 이 한 함수를 거친다(2026-09-28 팀장 결정). 종전 장비 탭은 수를 안 넘겨
 *    실물 TC 가 0/9 인데도 랙을 가득으로 그렸다(서랍은 비어 있었다).
 * 규칙은 `deckOccupancy()` 그대로다 — 0 이면 전부 비움 · 가득이면 전부 가득 · 그 사이는 칸을 고르지 않는다.
 * 팁은 합계 우선(`supplySummary` 의 `totalDecides`)이 이미 항목 값에 들어 있다.
 */
export function rackOccupancy(
  summary: SupplySummary,
  rack: { kind: DeckRackKind; labwareId: string },
): RackOccupancy {
  const item = summary.items.find((entry) =>
    entry.racks.some((r) => r.labwareId === rack.labwareId),
  )
  return deckOccupancy({
    kind: rack.kind,
    labwareId: rack.labwareId,
    count: reportedCount(item),
    usedWells: item?.usedWells,
  })
}

/** 팁 랙 «부족» 기준 — 남은 비율 20% 이하(디자인팀 시안 2026-10-01 · `DESIGN-CHANGES.md` §3 「팁 20% 이하 ▲ 부족」) */
export const TIPS_LOW_RATIO = 0.2

/**
 * **팁 랙이 부족한가** — 요약 카드의 「▲ 부족」 표지와 판이 같이 부른다(판정은 여기 한 곳).
 * 🔴 팁 랙에만 있는 판정이다 — TC 적재소의 부족 기준은 시안이 「결정 필요」로 남겼고(0/9 가 정상인지), 시약은 채움 여부다.
 * 🔴 모르는 수(`null` · 랙별 미수신 · 불일치)는 부족이 «아니다» — 없는 사실로 경고하지 않는다.
 */
export function isTipsLow(item: Pick<SupplyItem, 'key' | 'remaining' | 'total'>): boolean {
  if (item.key !== 'tips-1' && item.key !== 'tips-2') return false
  if (item.remaining === null || item.total <= 0) return false
  return item.remaining / item.total <= TIPS_LOW_RATIO
}

/**
 * **팁 · TC 가 다 떨어졌는가** — 남은 수가 정확히 0(eric 2026-10-03 「없거나.. 그러면.. 오른쪽에.. 노란색이나 빨간색.. 세모」).
 * 🔴 모르는 수(`null` · 랙별 미수신 · 불일치)는 «없음»이 아니다 — 없는 사실로 경고하지 않는다. 시약은 이 판정이 없다(칸마다 있음 · 없음).
 */
export function isSupplyEmpty(
  item: Pick<SupplyItem, 'key' | 'remaining' | 'total' | 'mismatch'>,
): boolean {
  if (item.key === 'reagents' || item.mismatch === true) return false
  return item.remaining === 0 && item.total > 0
}

/**
 * 머리 수치 오른쪽의 경고 — 없음(빨간 ▲) 이 부족(노란 ▲)보다 먼저다. 넉넉하면 `undefined`(표지 없음). 판정은 위 두 함수 그대로.
 */
export type SupplyAlert = 'empty' | 'low'

export function supplyAlert(
  item: Pick<SupplyItem, 'key' | 'remaining' | 'total' | 'mismatch'>,
): SupplyAlert | undefined {
  if (isSupplyEmpty(item)) return 'empty'
  if (isTipsLow(item)) return 'low'
  return undefined
}

/**
 * 장비 값이 하나도 안 온 시약 수(`presence: 'unreceived'`) — 카드 머리의 「미수신 N」. 시약 단위다(웰 수가 아니다 · 2026-10-02).
 * 레시피가 없으면 0(시약 자체가 없다)
 */
export function unreceivedReagentCount(summary: Pick<SupplySummary, 'reagents'>): number {
  return summary.reagents.filter((reagent) => reagent.presence === 'unreceived').length
}

export function supplyPercent(item: Pick<SupplyItem, 'remaining' | 'total'>): number | undefined {
  if (item.remaining === null || item.total <= 0) return undefined
  return (item.remaining / item.total) * 100
}

/**
 * **시약 카드의 랙 하나 — 고르기 선택지**(eric 2026-10-02 「염색기.. 장비쪽에.. 튜브랙 두개랑 콜드랙이 잇을 텐데... 이게.. 시약에 다
 * 못들어가니까.. 3개 랙 중에.. 하나식 선택해서 쓸 수 잇게 해줘..」).
 * `rack` 은 염색기 데크의 시약 랙(`deckRacks.ts` — 튜브 랙 1 = `ambient.1` · 튜브 랙 2 = `ambient.2` · 냉장 = `chilled`),
 * `bay` 는 도는 작업 레시피에서 그 랙에 놓인 시약 묶음(`reagentBays`)이다. 둘 중 하나는 늘 있다.
 */
export interface ReagentRackChoice {
  /** 선택지 키 — 랙이면 그 `labware_id`, 레시피에만 있는 시약대면 그 `labware_id`(없으면 빈 문자열) */
  key: string
  rack: DeckRack | undefined
  bay: ReagentBay | undefined
  /**
   * 선택지에 붙는 보관 온도(「4℃」) — 🔴 도는 작업 레시피 값(`ReagentBay.storageCelsius`)이 먼저, 레시피가 그 랙의 온도를 하나도 안 적었으면
   * (도는 작업 없음 · 그 랙에 시약 없음) 냉장 시약대만 염색기 설정값(`SupplySummary.storageSetpointCelsius`). 둘 다 없으면 `undefined`(빼고 그린다).
   * 레시피 시약 온도가 섞인 랙은 줄마다 제 온도를 적으므로 설정값을 얹지 않는다(같은 랙에 다른 두 온도가 서지 않게)
   */
  storageCelsius: number | undefined
}

/**
 * 시약 보관 능력(`reagent_storage`)이 맡는 시약대 — 냉장 시약대(cold rack) 하나다. 상온 튜브 랙에는 설정값을 붙이지 않는다
 * (데크 표 `deckRacks.ts` 의 `coldrack` 줄 `labwareId`).
 */
const CHILLED_BAY = 'staining.reagent_bay.chilled'

/**
 * **시약 카드의 랙 선택지** — 차례는 데크 랙 차례(튜브 랙 1 → 튜브 랙 2 → 냉장)이고, 레시피에만 있는 시약대(데크 표에 없는 자리 ·
 * 시약대 미지정)는 그 뒤에 `reagentBays` 차례로 붙는다(그 시약이 화면에서 사라지지 않게).
 * 🔴 데크 랙은 레시피에 그 랙 시약이 없어도 선택지에 선다 — 랙은 장비의 구성 요소다(비전 탭 규칙과 같은 결 · 빈 랙은 «없음»을 적는다).
 * 🔴 값을 새로 셈하지 않는다 — `reagentBays` 를 랙에 붙이기만 한다.
 */
export function reagentRackChoices(
  item: Pick<SupplyItem, 'racks'>,
  bays: readonly ReagentBay[],
  /** 염색기 시약 보관 설정 온도(`SupplySummary.storageSetpointCelsius`) — 냉장 시약대의 레시피 온도가 없을 때만 쓴다 */
  setpointCelsius?: number,
): ReagentRackChoice[] {
  const racks = item.racks.filter((rack) => rack.kind !== 'tips' && rack.kind !== 'tcstock')
  const used = new Set<ReagentBay>()
  const storageOf = (bay: ReagentBay | undefined, labwareId: string | undefined) => {
    if (bay?.storageCelsius !== undefined) return bay.storageCelsius
    const recipeTemps = (bay?.reagents ?? []).some((r) => r.storageCelsius !== undefined)
    // 시약대 ↔ 랙 대조는 접미 점 · 하이픈을 같은 것으로 본다(`deckRacks.labwareKey` 머리말)
    const chilled = sameLabware(labwareId, CHILLED_BAY)
    return chilled && !recipeTemps ? setpointCelsius : undefined
  }
  const choices: ReagentRackChoice[] = racks.map((rack) => {
    const bay = bays.find((b) => sameLabware(b.labwareId, rack.labwareId))
    if (bay !== undefined) used.add(bay)
    return { key: rack.labwareId, rack, bay, storageCelsius: storageOf(bay, rack.labwareId) }
  })
  for (const bay of bays) {
    if (!used.has(bay))
      choices.push({
        key: bay.labwareId ?? '',
        rack: undefined,
        bay,
        storageCelsius: storageOf(bay, bay.labwareId),
      })
  }
  return choices
}

/**
 * 선택한 랙의 시약 수 — 카드 머리의 「있음/시약 수」 · 「미수신 N」. 규칙은 판 전체와 같다(있음만 센다 · 미수신은 시약 단위).
 */
export function reagentRackCounts(bay: ReagentBay | undefined): {
  present: number
  total: number
  unreceived: number
} {
  const reagents = bay?.reagents ?? []
  return {
    present: reagents.filter((r) => r.presence === 'present').length,
    total: reagents.length,
    unreceived: unreceivedReagentCount({ reagents }),
  }
}

/** 칸 하나의 있음 · 없음 · 미수신 → 랙 그림의 칸 상태 — 있음 = 뚜껑(가득) · 없음 = 빈 구멍 · 미수신 = 옅은 채움(모름) */
const PRESENCE_FILL: Readonly<Record<ReagentPresence, WellFill>> = {
  present: 'full',
  absent: 'used',
  unreceived: 'unknown',
}

/**
 * **시약 카드의 랙 그림에 얹을 값**(eric 2026-10-02 「시약 탭 누를 때마다... 그림도 같이 보여달라고 했잖아.」) — 고른 랙의 시약이 놓인
 * 칸마다 칸 표지(「A1 ●」 · `wellPresence()`)와 **같은 판정**을 그림의 칸 상태로 옮긴다(`PRESENCE_FILL`). 시약이 담긴 칸에는 뚜껑 테
 * (`reagents` · 레시피 표시색 · 없으면 무채색)가 선다.
 * 🔴 레시피 시약이 없는 칸은 맵에 넣지 않는다 — 그림의 기본(「모르면 가득」 · eric 2026-09-27 · 장비 탭 그림과 같다)대로 선다.
 *    응답에 없는 상태를 짓지 않는다. 테가 없는 것으로 「이 칸은 도는 작업 시약이 아니다」가 갈린다(모양으로).
 * 🔴 미수신을 가득으로 접지 않는다 — 줄 목록이 미수신이라 말하는 칸을 그림이 «있음»이라 말하면 둘이 갈린다.
 */
export function reagentRackFigure(bay: ReagentBay | undefined): {
  occupancy: RackOccupancy
  reagents: RackReagents
} {
  const occupancy: Record<string, WellFill> = {}
  const reagents: Record<string, string | null> = {}
  for (const reagent of bay?.reagents ?? []) {
    for (const well of reagent.wells) {
      occupancy[well.well] = PRESENCE_FILL[wellPresence(well)]
      reagents[well.well] = reagent.color ?? null
    }
  }
  return { occupancy, reagents }
}

/*
 * ─── 랙 그림의 축 라벨(엑셀 표처럼 머리 글자 · 왼쪽 숫자) ─────────────────────────────────────────────
 * eric 2026-10-02 「오른쪽 소모품에서.. 사용자가 바로 알기 쉽게.. A, B, C 이거.. 엑셀의 테이블처럼.. 헤드와.. 왼쪽에.. 숫자를 적어줄래?」
 */

/** 라벨 하나 — 글자 · 그 칸 줄의 자리(그림 폭 또는 높이에 대한 0~1 비율) */
export interface RackAxisLabel {
  label: string
  at: number
}

/**
 * 축 라벨 — `head` 는 그림 위 머리 줄, `side` 는 그림 왼쪽 줄. `bands` 는 머리 줄과 칸 자리가 다른 «둘째 이후 묶음»의 머리
 * (10구 튜브 랙의 50 mL 2×2 처럼 같은 글자가 다른 가로 자리에 서는 묶음) — `at` 은 그 묶음 바로 위 틈의 세로 자리다.
 */
export interface RackAxes {
  head: RackAxisLabel[]
  side: RackAxisLabel[]
  bands: { at: number; labels: RackAxisLabel[] }[]
  /**
   * 글자가 왼쪽 줄(행)이고 숫자가 머리 줄(열)인가 — 돌리지 않은 Opentrons 표준 방향(eric 2026-10-12 「A, B, C 이게 행이 되어야」).
   * 이때 `bands` 는 둘째 묶음의 «세로» 글자 줄이다(`at` 은 그 묶음 바로 왼쪽 틈의 가로 자리 · 라벨 `at` 은 세로 자리)
   */
  lettersSide?: boolean
}

/** 그림 칸 하나 — 칸 이름(`A1` …)과 그림 좌표의 중심 */
export interface RackAxisCell {
  name: string
  cx: number
  cy: number
}

const AXIS_TOL = 0.5
const WELL_NAME = /^([A-Z]+)(\d+)$/u

/**
 * **칸 이름에서 축 라벨을 짓는다** — 🔴 라벨은 실제 칸 이름의 글자 · 숫자다(지어내지 않는다). 글자가 붙는 축은 «같은 숫자의 칸들이
 * 갈리는 방향»으로 정하고(가로여야 머리 줄이다), 자리는 그 글자 · 숫자를 가진 칸 중심이다(그림과 같은 좌표).
 * - 칸 이름이 글자+숫자 꼴이 아니면(TC rack 의 `'1'…'9'` — 자리 차례가 레시피에 없다) `null` — 라벨을 세우지 않는다
 * - 같은 숫자 줄이 한 자리에 모이지 않으면(격자가 아니면) `null`
 * - 글자 자리가 숫자 줄마다 다르면 이어지는 숫자 줄끼리 묶음을 나눠 둘째 묶음부터 `bands` 로 준다
 */
export function rackAxes(
  width: number,
  height: number,
  cells: readonly RackAxisCell[],
): RackAxes | null {
  if (width <= 0 || height <= 0 || cells.length === 0) return null
  const parsed: { letter: string; number: number; cx: number; cy: number }[] = []
  for (const cell of cells) {
    const m = WELL_NAME.exec(cell.name)
    if (m?.[1] === undefined || m[2] === undefined) return null
    parsed.push({ letter: m[1], number: Number(m[2]), cx: cell.cx, cy: cell.cy })
  }
  /* 같은 숫자 칸들이 어느 방향으로 갈리나 — 가로면 글자가 머리 줄(가로), 숫자가 왼쪽 줄(세로) */
  let spreadX = 0
  let spreadY = 0
  const byNumber = new Map<number, typeof parsed>()
  for (const p of parsed) {
    const group = byNumber.get(p.number)
    if (group === undefined) byNumber.set(p.number, [p])
    else group.push(p)
  }
  for (const group of byNumber.values()) {
    const xs = group.map((p) => p.cx)
    const ys = group.map((p) => p.cy)
    spreadX += Math.max(...xs) - Math.min(...xs)
    spreadY += Math.max(...ys) - Math.min(...ys)
  }
  /*
   * 글자가 세로로 서는 그림(돌리지 않은 Opentrons 표준 방향 · 소모품 판이 2026-10-12 하루 썼다 — 10-13 부터 다시 세로라 지금은 부르는 곳이 없다)은 좌표를 맞바꿔 같은 셈을 한 뒤 머리 줄 · 왼쪽 줄을
   * 바꿔 돌려준다 — 글자가 왼쪽 줄(행) · 숫자가 머리 줄(열)
   */
  if (spreadX < spreadY) {
    const swapped = rackAxes(
      height,
      width,
      cells.map((cell) => ({ name: cell.name, cx: cell.cy, cy: cell.cx })),
    )
    return swapped === null
      ? null
      : { head: swapped.side, side: swapped.head, bands: swapped.bands, lettersSide: true }
  }
  const letterPos = (p: { cx: number; cy: number }) => p.cx
  const numberPos = (p: { cx: number; cy: number }) => p.cy
  const letterSpan = width
  const numberSpan = height
  /* 숫자 줄 — 한 숫자의 칸은 한 자리에 모여야 한다 */
  const rows: { number: number; pos: number; letters: Map<string, number> }[] = []
  for (const [number, group] of byNumber) {
    const ps = group.map(numberPos)
    if (Math.max(...ps) - Math.min(...ps) > AXIS_TOL) return null
    rows.push({
      number,
      pos: ps.reduce((a, b) => a + b, 0) / ps.length,
      letters: new Map(group.map((p) => [p.letter, letterPos(p)])),
    })
  }
  rows.sort((a, b) => a.pos - b.pos)
  const same = (a: Map<string, number>, b: Map<string, number>) =>
    a.size === b.size &&
    [...a].every(([letter, pos]) => {
      const other = b.get(letter)
      return other !== undefined && Math.abs(other - pos) <= AXIS_TOL
    })
  const labelsOf = (letters: Map<string, number>): RackAxisLabel[] =>
    [...letters]
      .map(([label, pos]) => ({ label, at: pos / letterSpan }))
      .sort((a, b) => a.at - b.at)
  const first = rows[0]
  if (first === undefined) return null
  const bands: RackAxes['bands'] = []
  let current = first.letters
  for (let i = 1; i < rows.length; i++) {
    const row = rows[i]
    const prev = rows[i - 1]
    if (row === undefined || prev === undefined) continue
    if (!same(row.letters, current)) {
      current = row.letters
      bands.push({ at: (prev.pos + row.pos) / 2 / numberSpan, labels: labelsOf(row.letters) })
    }
  }
  return {
    head: labelsOf(first.letters),
    side: rows.map((row) => ({ label: String(row.number), at: row.pos / numberSpan })),
    bands,
  }
}

/** 축 글자 하나의 «그려진» 자리 · 크기(화면 px) — 솎기(`thinAxisLabels`)의 입력 */
export interface AxisLabelBox {
  label: string
  /** 축 방향 중심 자리 */
  pos: number
  /** 축 방향 글자 크기(머리 줄 = 폭 · 왼쪽 줄 = 높이) */
  size: number
}

/**
 * **축 글자 솎기** — 칸이 촘촘해 이웃 글자가 포개지면(팁 랙 왼쪽 1~12 가 작은 카드에서 10 · 11 · 12 로 겹쳤다 · 2026-10-26) k 개마다 하나만 남긴다.
 * 🔴 글자 크기를 최소 단 밑으로 줄이지 않는다 — 줄 대신 솎는다. 남기는 기준은 첫 칸(`A` · `1`)이라 k = 2 면 1 · 3 · 5 … 다.
 * 이웃 둘의 중심 거리가 두 글자 반 폭의 합 + `gap` 이상이 되는 가장 작은 k 를 고른다. 돌려주는 배열은 입력 차례 그대로 «남김» 여부.
 */
export function thinAxisLabels(boxes: readonly AxisLabelBox[], gap: number): boolean[] {
  const n = boxes.length
  if (n <= 1) return boxes.map(() => true)
  const order = boxes.map((_b, i) => i).sort((a, b) => (boxes[a]?.pos ?? 0) - (boxes[b]?.pos ?? 0))
  const first = boxes
    .map((b, i) => ({ label: b.label, i }))
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }))[0]?.i
  const anchor = Math.max(0, order.indexOf(first ?? 0))
  for (let k = 1; k <= n; k++) {
    const kept = order.filter((_i, rank) => Math.abs(rank - anchor) % k === 0)
    const fits = kept.every((cur, j) => {
      const next = kept[j + 1]
      const a = boxes[cur]
      const b = next === undefined ? undefined : boxes[next]
      return a === undefined || b === undefined || b.pos - a.pos >= (a.size + b.size) / 2 + gap
    })
    if (fits) {
      const keep = new Set(kept)
      return boxes.map((_b, i) => keep.has(i))
    }
  }
  return boxes.map((_b, i) => i === first)
}
